export interface HealthResponse {
  status: 'ok' | string;
  message?: string;
}

export type ParsedReadiness =
  | { kind: 'healthy' }
  /** Backstage root startup has not finished; HTTP server may already be up. */
  | { kind: 'starting'; message?: string }
  | { kind: 'unhealthy'; health: HealthResponse }
  | { kind: 'failed'; error: Error };

const BACKEND_STARTUP_MESSAGE = 'Backend has not started yet';

export async function parseReadinessResponse(
  response: Response,
): Promise<ParsedReadiness> {
  const bodyText = await response.text();
  let health: HealthResponse | undefined;

  if (bodyText) {
    try {
      health = JSON.parse(bodyText) as HealthResponse;
    } catch {
      // Non-JSON readiness body; fall through to generic handling.
    }
  }

  if (response.ok && health?.status === 'ok') {
    return { kind: 'healthy' };
  }

  if (
    response.status === 503 &&
    health?.message === BACKEND_STARTUP_MESSAGE
  ) {
    return { kind: 'starting', message: health.message };
  }

  if (response.ok && health) {
    return { kind: 'unhealthy', health };
  }

  return {
    kind: 'failed',
    error: new Error(
      `Readiness check failed: ${response.status} ${response.statusText}${
        bodyText ? ` — ${bodyText.slice(0, 500)}` : ''
      }`,
    ),
  };
}

/** True when the API is reachable enough for typical local dev (sign-in, browsing). */
export async function probeBackendUsable(baseUrl: string): Promise<boolean> {
  const base = baseUrl.replace(/\/$/, '');

  try {
    const [liveness, auth] = await Promise.all([
      fetch(`${base}/.backstage/health/v1/liveness`),
      fetch(`${base}/api/auth/guest/refresh`, { method: 'POST' }),
    ]);
    return liveness.ok && auth.ok;
  } catch {
    return false;
  }
}
